import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { handleStoreAction } from '../utils/storeHelpers';
import { netlifyFunctionsBase } from '../utils/netlifyFunctionsBase';
import { useAuthStore } from './useAuthStore';

export interface ProductoGlobal {
  id: string;
  ean: string;
  nombre: string;
  marca: string | null;
  fabricante: string | null;
  categoria: string | null;
  subcategoria: string | null;
  tamano: number | null;
  unidad_medida: string | null;
  unidades_por_empaque: number;
  tipo_empaque: string | null;
  pais_origen: string | null;
  familia_producto: string | null;
  sku_tipo: 'Unidad' | 'Multipack';
  precio_sugerido: number | null;
  image_url: string | null;
  fuente: 'seed' | 'openfoodfacts' | 'usuario' | 'csv_import';
  verificado: boolean;
  contribuido_por: string | null;
  created_at: string;
  updated_at: string;
}

export interface BarcodeLookupResult {
  found: boolean;
  source: 'local' | 'openfoodfacts' | 'none';
  product: ProductoGlobal | null;
}

export interface ProductoGlobalStats {
  total_productos: number;
  verificados: number;
  from_seed: number;
  from_off: number;
  from_usuarios: number;
  from_csv: number;
  categorias: number;
  marcas: number;
  fabricantes: number;
}

export interface OFFImportBatchResult {
  processed: number;
  inserted: number;
  skipped: number;
  errors: number;
  errorDetails: string[];
  currentPage: number;
  nextPage: number | null;
  totalPages: number;
  totalProducts: number;
  done: boolean;
  batchSize: number;
}

function rowToProductoGlobal(r: Record<string, unknown>): ProductoGlobal {
  return {
    id: String(r.id),
    ean: String(r.ean),
    nombre: String(r.nombre),
    marca: (r.marca as string) ?? null,
    fabricante: (r.fabricante as string) ?? null,
    categoria: (r.categoria as string) ?? null,
    subcategoria: (r.subcategoria as string) ?? null,
    tamano: r.tamano != null ? Number(r.tamano) : null,
    unidad_medida: (r.unidad_medida as string) ?? null,
    unidades_por_empaque: Number(r.unidades_por_empaque ?? 1),
    tipo_empaque: (r.tipo_empaque as string) ?? null,
    pais_origen: (r.pais_origen as string) ?? null,
    familia_producto: (r.familia_producto as string) ?? null,
    sku_tipo: r.sku_tipo === 'Multipack' ? 'Multipack' : 'Unidad',
    precio_sugerido: r.precio_sugerido != null ? Number(r.precio_sugerido) : null,
    image_url: (r.image_url as string) ?? null,
    fuente: (r.fuente as ProductoGlobal['fuente']) || 'usuario',
    verificado: Boolean(r.verificado),
    contribuido_por: (r.contribuido_por as string) ?? null,
    created_at: String(r.created_at ?? ''),
    updated_at: String(r.updated_at ?? ''),
  };
}

interface Filters {
  categoria: string;
  fuente: string;
  verificado: string;
}

interface ProductoGlobalState {
  productos: ProductoGlobal[];
  loading: boolean;
  error: string | null;
  stats: ProductoGlobalStats | null;
  searchQuery: string;
  filters: Filters;
  fetchProductos: () => Promise<void>;
  fetchStats: () => Promise<void>;
  buscarPorBarcode: (ean: string) => Promise<BarcodeLookupResult>;
  crearProducto: (data: Partial<ProductoGlobal>) => Promise<ProductoGlobal | null>;
  actualizarProducto: (id: string, data: Partial<ProductoGlobal>) => Promise<ProductoGlobal | null>;
  eliminarProducto: (id: string) => Promise<boolean>;
  importarCSV: (file: File) => Promise<{ inserted: number; errors: string[] }>;
  importFromOFF: (startPage: number, batchSize?: number) => Promise<OFFImportBatchResult>;
  setSearch: (q: string) => void;
  setFilters: (f: Partial<Filters>) => void;
  clearError: () => void;
}

const defaultFilters: Filters = { categoria: '', fuente: '', verificado: '' };

export const useProductoGlobalStore = create<ProductoGlobalState>((set, get) => ({
  productos: [],
  loading: false,
  error: null,
  stats: null,
  searchQuery: '',
  filters: { ...defaultFilters },

  clearError: () => set({ error: null }),

  setSearch: (q: string) => {
    set({ searchQuery: q });
    void get().fetchProductos();
  },

  setFilters: (f: Partial<Filters>) => {
    set({ filters: { ...get().filters, ...f } });
    void get().fetchProductos();
  },

  fetchStats: async () => {
    console.log('📦 Fetching producto_global_stats...');
    const { data, error } = await supabase.from('producto_global_stats').select('*').maybeSingle();
    if (error) {
      console.error(error);
      set({ stats: null, error: error.message });
      return;
    }
    if (!data) {
      set({ stats: null });
      return;
    }
    const n = (v: unknown) => Number(v ?? 0);
    set({
      stats: {
        total_productos: n(data.total_productos),
        verificados: n(data.verificados),
        from_seed: n(data.from_seed),
        from_off: n(data.from_off),
        from_usuarios: n(data.from_usuarios),
        from_csv: n(data.from_csv),
        categorias: n(data.categorias),
        marcas: n(data.marcas),
        fabricantes: n(data.fabricantes),
      },
    });
  },

  fetchProductos: async () => {
    await handleStoreAction(
      async () => {
        console.log('📦 Fetching productos globales...');
        const { searchQuery, filters } = get();
        let q = supabase.from('producto_global').select('*').order('created_at', { ascending: false }).limit(100);
        const sq = searchQuery.trim();
        if (sq) {
          const esc = sq.replace(/%/g, '\\%').replace(/_/g, '\\_');
          q = q.or(`nombre.ilike.%${esc}%,ean.ilike.%${esc}%,marca.ilike.%${esc}%`);
        }
        if (filters.categoria) q = q.eq('categoria', filters.categoria);
        if (filters.fuente) q = q.eq('fuente', filters.fuente);
        if (filters.verificado === 'yes') q = q.eq('verificado', true);
        if (filters.verificado === 'no') q = q.eq('verificado', false);
        const { data, error } = await q;
        if (error) throw error;
        set({ productos: (data || []).map(rowToProductoGlobal) });
      },
      (err) => set({ error: err }),
      (loading) => set({ loading }),
    );
  },

  importFromOFF: async (startPage: number, batchSize = 5) => {
    const base = netlifyFunctionsBase();
    const url = `${base}/off-colombia-import`;
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ startPage, batchSize }),
    });
    let body: Record<string, unknown>;
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      throw new Error('Respuesta inválida del servidor');
    }
    if (!res.ok) {
      throw new Error(String(body.error || `HTTP ${res.status}`));
    }
    return {
      processed: Number(body.processed ?? 0),
      inserted: Number(body.inserted ?? 0),
      skipped: Number(body.skipped ?? 0),
      errors: Number(body.errors ?? 0),
      errorDetails: Array.isArray(body.errorDetails) ? (body.errorDetails as string[]) : [],
      currentPage: Number(body.currentPage ?? startPage),
      nextPage: body.nextPage === null ? null : Number(body.nextPage),
      totalPages: Number(body.totalPages ?? 0),
      totalProducts: Number(body.totalProducts ?? 0),
      done: Boolean(body.done),
      batchSize: Number(body.batchSize ?? batchSize),
    } as OFFImportBatchResult;
  },

  buscarPorBarcode: async (ean: string) => {
    const base = netlifyFunctionsBase();
    const url = `${base}/barcode-lookup`;
    console.log('📦 buscarPorBarcode →', url, ean);
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ barcode: ean.trim() }),
    });
    let body: Record<string, unknown>;
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      throw new Error('Respuesta inválida del servidor');
    }
    if (!res.ok) {
      throw new Error(String(body.error || `HTTP ${res.status}`));
    }
    const found = Boolean(body.found);
    const source = (body.source as BarcodeLookupResult['source']) || 'none';
    const product = body.product ? rowToProductoGlobal(body.product as Record<string, unknown>) : null;
    return { found, source, product } as BarcodeLookupResult;
  },

  crearProducto: async (data: Partial<ProductoGlobal>) => {
    const empresaId = useAuthStore.getState().currentEmpresa?.id ?? null;
    const row = {
      ean: data.ean?.trim(),
      nombre: data.nombre?.trim(),
      marca: data.marca ?? null,
      fabricante: data.fabricante ?? null,
      categoria: data.categoria ?? null,
      subcategoria: data.subcategoria ?? null,
      tamano: data.tamano ?? null,
      unidad_medida: data.unidad_medida ?? null,
      unidades_por_empaque: data.unidades_por_empaque ?? 1,
      tipo_empaque: data.tipo_empaque ?? null,
      pais_origen: data.pais_origen ?? 'CO',
      familia_producto: data.familia_producto ?? null,
      sku_tipo: data.sku_tipo === 'Multipack' ? 'Multipack' : 'Unidad',
      precio_sugerido: data.precio_sugerido ?? null,
      image_url: data.image_url ?? null,
      fuente: 'usuario' as const,
      verificado: data.verificado ?? false,
      contribuido_por: empresaId,
    };
    if (!row.ean || !row.nombre) throw new Error('EAN y nombre son obligatorios');
    const { data: inserted, error } = await supabase.from('producto_global').insert(row).select('*').single();
    if (error) throw error;
    await get().fetchProductos();
    await get().fetchStats();
    return rowToProductoGlobal(inserted as Record<string, unknown>);
  },

  actualizarProducto: async (id: string, data: Partial<ProductoGlobal>) => {
    const patch: Record<string, unknown> = { ...data };
    delete patch.id;
    delete patch.created_at;
    delete patch.updated_at;
    const { data: updated, error } = await supabase
      .from('producto_global')
      .update(patch)
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;
    await get().fetchProductos();
    await get().fetchStats();
    return rowToProductoGlobal(updated as Record<string, unknown>);
  },

  eliminarProducto: async (id: string) => {
    const { error } = await supabase.from('producto_global').delete().eq('id', id);
    if (error) throw error;
    await get().fetchProductos();
    await get().fetchStats();
    return true;
  },

  importarCSV: async (file: File) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const errors: string[] = [];
    if (lines.length < 2) {
      return { inserted: 0, errors: ['El archivo no tiene filas de datos'] };
    }
    const headerLine = lines[0];
    if (!headerLine) return { inserted: 0, errors: ['CSV vacío'] };
    const header = headerLine.split(',').map((h) => h.trim().toLowerCase());
    const idx = (name: string) => header.indexOf(name);
    const need = ['ean', 'nombre'];
    for (const n of need) {
      if (idx(n) < 0) errors.push(`Falta columna: ${n}`);
    }
    if (errors.length) return { inserted: 0, errors };

    const rows: Record<string, unknown>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const rowLine = lines[i];
      if (!rowLine) continue;
      const parts = rowLine.split(',');
      const getv = (col: string) => {
        const j = idx(col);
        return j >= 0 ? (parts[j] || '').trim() : '';
      };
      const ean = getv('ean');
      const nombre = getv('nombre');
      if (!ean || !nombre) {
        errors.push(`Fila ${i + 1}: ean y nombre requeridos`);
        continue;
      }
      rows.push({
        ean,
        nombre,
        marca: getv('marca') || null,
        fabricante: getv('fabricante') || null,
        categoria: getv('categoria') || null,
        subcategoria: getv('subcategoria') || null,
        tamano: getv('tamano') ? Number(getv('tamano').replace(',', '.')) : null,
        unidad_medida: getv('unidad_medida') || null,
        unidades_por_empaque: getv('unidades_por_empaque') ? parseInt(getv('unidades_por_empaque'), 10) || 1 : 1,
        tipo_empaque: getv('tipo_empaque') || null,
        pais_origen: getv('pais_origen') || 'CO',
        familia_producto: getv('familia_producto') || null,
        sku_tipo: getv('sku_tipo') === 'Multipack' ? 'Multipack' : 'Unidad',
        precio_sugerido: getv('precio_sugerido') ? Number(getv('precio_sugerido').replace(',', '.')) : null,
        image_url: getv('image_url') || null,
        fuente: 'csv_import',
        verificado: (() => {
          const v = getv('verificado');
          return v === '1' || String(v).toLowerCase() === 'true';
        })(),
        contribuido_por: useAuthStore.getState().currentEmpresa?.id ?? null,
      });
    }
    if (!rows.length) return { inserted: 0, errors: errors.length ? errors : ['Sin filas válidas'] };

    let inserted = 0;
    const chunk = 40;
    for (let i = 0; i < rows.length; i += chunk) {
      const slice = rows.slice(i, i + chunk);
      const { error } = await supabase.from('producto_global').insert(slice);
      if (error) {
        errors.push(`Lote ${i / chunk + 1}: ${error.message}`);
        break;
      }
      inserted += slice.length;
    }
    await get().fetchProductos();
    await get().fetchStats();
    return { inserted, errors };
  },
}));
